<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" @close="closeDialog" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/30 dark:bg-black/50 backdrop-blur-xs" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full max-w-xl transform overflow-hidden rounded-2xl bg-white dark:bg-zinc-900 p-6 text-left align-middle shadow-xl transition-all">
              <DialogTitle as="h3" class="text-lg font-medium leading-6 text-gray-900 dark:text-zinc-100 flex items-center justify-between">
                <span>Your Cart ({{ cartStore.cartCount }})</span>
                <button
                  @click="closeDialog"
                  class="text-gray-400 hover:text-gray-500 dark:text-zinc-400 dark:hover:text-zinc-300 transition-colors"
                >
                  <span class="sr-only">Close</span>
                  <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                </button>
              </DialogTitle>

              <!-- Cart Items -->
              <div v-if="cartStore.cartCount > 0" class="mt-4 divide-y divide-gray-200 dark:divide-zinc-700 max-h-96 overflow-y-auto">
                <div v-for="item in cartStore.cartItems" :key="item.id" class="flex py-4 space-x-4">
                  <div class="w-24 h-16 shrink-0 bg-muted rounded-lg overflow-hidden">
                    <img
                      :src="item.image || '/placeholder-car.jpg'"
                      :alt="item.title || 'Vehicle'"
                      class="w-full h-full object-cover"
                    />
                  </div>
                  <div class="flex-1 min-w-0">
                    <h4 class="text-sm font-medium text-gray-900 dark:text-zinc-100 truncate">{{ item.title || 'Vehicle Placeholder' }}</h4>
                    <div class="mt-1 flex items-center justify-between">
                      <span class="text-sm font-medium text-gray-900 dark:text-zinc-100">${{ (item.price || 0).toLocaleString() }}</span>
                      <button @click="cartStore.removeFromCart(item.id)" class="text-gray-400 hover:text-red-500 dark:text-zinc-500 dark:hover:text-red-400 transition-colors">
                        <TrashIcon class="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-center py-12">
                <ShoppingCartIcon class="mx-auto h-12 w-12 text-gray-300 dark:text-zinc-600" />
                <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-zinc-100">Your cart is empty</h3>
                <p class="mt-1 text-sm text-gray-500 dark:text-zinc-400">Add vehicles to get started.</p>
              </div>

              <!-- Cart Summary -->
              <div v-if="cartStore.cartCount > 0" class="mt-6 border-t border-gray-200 dark:border-zinc-700/50 pt-4">
                <dl class="space-y-1 text-sm text-gray-500 dark:text-zinc-400">
                  <div class="flex justify-between border-t border-gray-200 dark:border-zinc-700/50 pt-2 font-medium text-gray-900 dark:text-zinc-100">
                    <dt>Total</dt>
                    <dd>${{ cartStore.cartTotal.toLocaleString() }}</dd>
                  </div>
                </dl>
                <div class="mt-4">
                  <button
                    @click="handleCheckout"
                    class="w-full rounded-lg bg-primary px-4 py-3 text-sm font-medium text-primary-foreground shadow-xs hover:bg-primary/90 transition-colors"
                  >
                    Proceed to Checkout
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { XMarkIcon, ShoppingCartIcon, TrashIcon } from '@heroicons/vue/24/outline'
import { useCartStore } from '~/stores/cartStore'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

const props = defineProps<{ isOpen: boolean }>()
const emit = defineEmits<{ (e: 'close'): void }>()

const router = useRouter()
const cartStore = useCartStore()

const closeDialog = () => {
  cartStore.closeCart()
  emit('close')
}

const handleCheckout = () => {
  // Navigate to contact page with checkout parameter
  router.push('/contact?checkout=true');
  cartStore.closeCart()
}
</script>

<style scoped>
/* Add any additional styles here */
</style>